import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { projectApi } from "../../backend/client";
import { useCurrentUser } from "../../backend/queries/auth";
import { invalidateDashboard } from "../../backend/queries/dashboard";
import { useAssignmentOptions } from "../../backend/queries/users";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { ChevronDown, Lock } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { optionsIncludingCurrent } from "../../lib/projectMetadata";
import { useProjectMetadata } from "../../lib/useProjectMetadata";

type RequiredField =
  | "customer_id"
  | "agent_id"
  | "project_type"
  | "status"
  | "project_start_date"
  | "relocating_from";

// The fields a project cannot be created without, in the order they appear,
// with the message shown under each one.
const REQUIRED_FIELDS: {
  field: RequiredField;
  label: string;
  message: string;
}[] = [
  {
    field: "customer_id",
    label: "Customer",
    message: "Choose a customer from the list.",
  },
  {
    field: "agent_id",
    label: "Staff",
    message: "Choose a staff member from the list.",
  },
  {
    field: "project_type",
    label: "Project type",
    message: "Choose a project type.",
  },
  {
    field: "status",
    label: "Status",
    message: "Choose a status.",
  },
  {
    field: "project_start_date",
    label: "Project start date",
    message: "Enter a start date.",
  },
  {
    field: "relocating_from",
    label: "Relocating from",
    message: "Enter where the client is moving from.",
  },
];

/** The visible asterisk is decorative; screen readers hear "required". */
const RequiredMark = () => (
  <>
    <span aria-hidden="true" className="text-tone-danger-foreground">
      {" *"}
    </span>
    <span className="sr-only"> (required)</span>
  </>
);

const FieldError = ({ field, message }: { field: string; message?: string }) =>
  message ? (
    <p id={`${field}_error`} className="text-sm text-destructive">
      {message}
    </p>
  ) : null;

type ProjectFormProps = {
  handleCloseForm: () => void;
  onSuccess?: () => void | Promise<void>;
};

const ProjectForm = ({ handleCloseForm, onSuccess }: ProjectFormProps) => {
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();
  // Rust makes an Agent the staff member on any project they create, so the
  // form says so instead of offering a choice it would discard.
  const isAgent = (currentUser?.profile?.role || currentUser?.role) === "Agent";
  const currentUserId = currentUser?.profile?.id || currentUser?.id;
  const {
    metadata,
    loading: metadataLoading,
    error: metadataError,
  } = useProjectMetadata();
  const [formData, setFormData] = useState({
    customer_id: "",
    agent_id: "",
    status: "",
    project_type: "",
    relocating_from: "",
    relocating_to: "",
    project_start_date: "",
    project_end_date: "",
    total_budget: "",
    project_notes: "",
  });

  const [customerSearch, setCustomerSearch] = useState("");
  const [agentSearch, setAgentSearch] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<RequiredField, string>>
  >({});
  // Focus goes to the error summary rather than the first field: focusing
  // the customer search would open its list over the messages below it.
  const summaryRef = useRef<HTMLDivElement>(null);
  const [summaryFocusRequest, setSummaryFocusRequest] = useState(0);
  useEffect(() => {
    if (summaryFocusRequest > 0) summaryRef.current?.focus();
  }, [summaryFocusRequest]);
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const debouncedCustomerSearch = useDebouncedValue(customerSearch);
  const debouncedAgentSearch = useDebouncedValue(agentSearch);
  const customerQuery = useAssignmentOptions(
    {
      kind: "customer",
      search: debouncedCustomerSearch.trim() || undefined,
      limit: 20,
    },
    { enabled: showCustomerDropdown },
  );
  const agentQuery = useAssignmentOptions(
    {
      kind: "agent",
      search: debouncedAgentSearch.trim() || undefined,
      limit: 20,
    },
    { enabled: showAgentDropdown },
  );
  const customerResults = customerQuery.data?.items || [];
  const agentAdminResults = agentQuery.data?.items || [];
  const peopleError = customerQuery.error?.message || agentQuery.error?.message;

  const clearFieldError = (field: string) =>
    setFieldErrors((previous) => {
      if (!(field in previous)) return previous;
      const next = { ...previous };
      delete next[field as RequiredField];
      return next;
    });

  /** Marks a control invalid and points it at its message. */
  const errorProps = (field: RequiredField) =>
    fieldErrors[field]
      ? { "aria-invalid": true, "aria-describedby": `${field}_error` }
      : {};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    clearFieldError(name);
  };

  const handleSelectUser = (user, type) => {
    setFormData((prevData) => ({
      ...prevData,
      [`${type}_id`]: user.id,
    }));
    clearFieldError(`${type}_id`);
    if (type === "customer") {
      setCustomerSearch(`${user.firstname} ${user.lastname}`);
      setShowCustomerDropdown(false);
    } else {
      setAgentSearch(`${user.firstname} ${user.lastname}`);
      setShowAgentDropdown(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    if (metadataLoading || metadataError) {
      setErrorMessage(metadataError || "Project options are still loading");
      setIsLoading(false);
      return;
    }

    // Checks every required field at once, so the user sees all of them
    // rather than fixing one and being told about the next.
    const missing = REQUIRED_FIELDS.filter(
      ({ field }) => !(field === "agent_id" && isAgent) && !formData[field],
    );
    if (missing.length > 0) {
      setFieldErrors(
        Object.fromEntries(
          missing.map(({ field, message }) => [field, message]),
        ),
      );
      setErrorMessage(
        `Fill in the required ${
          missing.length === 1 ? "field" : "fields"
        }: ${missing.map(({ label }) => label).join(", ")}.`,
      );
      setIsLoading(false);
      setSummaryFocusRequest((request) => request + 1);
      return;
    }
    setFieldErrors({});

    // Build clean payload (don’t mutate state)
    const payload = {
      ...formData,
      agent_id: isAgent ? currentUserId : formData.agent_id,
      project_end_date: formData.project_end_date || null,
      relocating_to: formData.relocating_to || null,
      project_notes: formData.project_notes || null,
      total_budget:
        formData.total_budget === "" || formData.total_budget == null
          ? 0
          : Number(formData.total_budget),
    };

    const { error } = await projectApi.create(payload);
    setIsLoading(false);

    if (error) {
      setErrorMessage("Error creating project: " + error.message);
    } else {
      await invalidateDashboard(queryClient);
      setSuccessMessage("Project successfully created!");
      if (typeof onSuccess === "function") {
        await onSuccess(); // parent: refresh list/details + close modal
      }
    }
  };

  const handleSuccessClose = async () => {
    if (typeof onSuccess === "function") {
      await onSuccess();
    } else {
      handleCloseForm();
    }
  };

  const updateField = (name, value) => {
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    clearFieldError(name);
  };

  return (
    <div className="space-y-5">
      <DialogHeader>
        <DialogTitle>Create new project</DialogTitle>
        <DialogDescription>
          Assign a client, owner, timeline, and relocation details. Fields
          marked * are required.
        </DialogDescription>
      </DialogHeader>

      {(errorMessage || metadataError || peopleError || successMessage) && (
        <div
          ref={summaryRef}
          tabIndex={-1}
          role={
            errorMessage || metadataError || peopleError ? "alert" : "status"
          }
          className={`rounded-md border px-3 py-2 text-sm ${
            errorMessage || metadataError || peopleError
              ? "border-destructive/30 bg-destructive/10 text-destructive"
              : "border-tone-success-border bg-tone-success-surface text-tone-success-foreground"
          }`}
        >
          {errorMessage || metadataError || peopleError || successMessage}
          {successMessage && (
            <button
              type="button"
              className="ml-3 underline"
              onClick={handleSuccessClose}
            >
              Close
            </button>
          )}
        </div>
      )}

      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="relative space-y-2">
            <Label htmlFor="customer_search">
              Customer
              <RequiredMark />
            </Label>
            <div className="relative">
              <Input
                id="customer_search"
                type="text"
                name="customer_search"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                onFocus={() => setShowCustomerDropdown(true)}
                onBlur={() =>
                  setTimeout(() => setShowCustomerDropdown(false), 200)
                }
                placeholder="Search or select a customer"
                className="!pr-10 aria-[invalid=true]:border-destructive"
                {...errorProps("customer_id")}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-10 w-10"
                onClick={() => setShowCustomerDropdown((s) => !s)}
              >
                <ChevronDown className="h-4 w-4" />
                <span className="sr-only">Toggle customer results</span>
              </Button>
            </div>
            {showCustomerDropdown && (
              <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-md border bg-popover p-1 text-sm shadow-md">
                {customerResults.map((customer) => (
                  <button
                    key={customer.id}
                    type="button"
                    className="flex w-full rounded-sm px-3 py-2 text-left hover:bg-accent"
                    onMouseDown={() => handleSelectUser(customer, "customer")}
                  >
                    {customer.firstname} {customer.lastname}
                  </button>
                ))}
                {customerQuery.isFetching ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    Searching...
                  </div>
                ) : (
                  customerResults.length === 0 && (
                    <div className="px-3 py-2 text-muted-foreground">
                      No results
                    </div>
                  )
                )}
              </div>
            )}
            <FieldError field="customer_id" message={fieldErrors.customer_id} />
          </div>

          {isAgent ? (
            // Read-only, in the same treatment as a locked field on the
            // project page: Rust assigns a project an Agent creates to them.
            <div className="space-y-2">
              <Label>Staff</Label>
              <div className="flex min-h-10 w-full items-center gap-2 rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground">
                <span className="min-w-0 flex-1">You</span>
                <span
                  title="Projects you create are assigned to you."
                  className="shrink-0 text-muted-foreground"
                >
                  <Lock className="h-4 w-4" aria-hidden="true" />
                  <span className="sr-only">
                    Projects you create are assigned to you.
                  </span>
                </span>
              </div>
            </div>
          ) : (
            <div className="relative space-y-2">
              <Label htmlFor="agent_search">
                Staff
                <RequiredMark />
              </Label>
              <div className="relative">
                <Input
                  id="agent_search"
                  type="text"
                  name="agent_search"
                  value={agentSearch}
                  onChange={(e) => setAgentSearch(e.target.value)}
                  onFocus={() => setShowAgentDropdown(true)}
                  onBlur={() =>
                    setTimeout(() => setShowAgentDropdown(false), 200)
                  }
                  placeholder="Search or select an owner"
                  className="!pr-10 aria-[invalid=true]:border-destructive"
                  {...errorProps("agent_id")}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-10 w-10"
                  onClick={() => setShowAgentDropdown((s) => !s)}
                >
                  <ChevronDown className="h-4 w-4" />
                  <span className="sr-only">Toggle owner results</span>
                </Button>
              </div>
              {showAgentDropdown && (
                <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-md border bg-popover p-1 text-sm shadow-md">
                  {agentAdminResults.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      className="flex w-full items-center justify-between gap-3 rounded-sm px-3 py-2 text-left hover:bg-accent"
                      onMouseDown={() => handleSelectUser(user, "agent")}
                    >
                      <span>
                        {user.firstname} {user.lastname}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {user.role}
                      </span>
                    </button>
                  ))}
                  {agentQuery.isFetching ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      Searching...
                    </div>
                  ) : (
                    agentAdminResults.length === 0 && (
                      <div className="px-3 py-2 text-muted-foreground">
                        No results
                      </div>
                    )
                  )}
                </div>
              )}
              <FieldError field="agent_id" message={fieldErrors.agent_id} />
            </div>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="project_type">
              Project type
              <RequiredMark />
            </Label>
            <Select
              value={formData.project_type}
              onValueChange={(value) => updateField("project_type", value)}
              disabled={metadataLoading || Boolean(metadataError)}
            >
              <SelectTrigger
                id="project_type"
                className="aria-[invalid=true]:border-destructive"
                {...errorProps("project_type")}
              >
                <SelectValue placeholder="Choose project type" />
              </SelectTrigger>
              <SelectContent>
                {optionsIncludingCurrent(
                  metadata.project_types,
                  formData.project_type,
                ).map((option) => (
                  <SelectItem key={option.code} value={option.code}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError
              field="project_type"
              message={fieldErrors.project_type}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="project_status">
              Status
              <RequiredMark />
            </Label>
            <Select
              value={formData.status}
              onValueChange={(value) => updateField("status", value)}
              disabled={metadataLoading || Boolean(metadataError)}
            >
              <SelectTrigger
                id="project_status"
                className="aria-[invalid=true]:border-destructive"
                {...errorProps("status")}
              >
                <SelectValue placeholder="Choose status" />
              </SelectTrigger>
              <SelectContent>
                {optionsIncludingCurrent(
                  metadata.statuses,
                  formData.status,
                ).map((option) => (
                  <SelectItem key={option.code} value={option.code}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError field="status" message={fieldErrors.status} />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="project_start_date">
              Project start date
              <RequiredMark />
            </Label>
            <Input
              id="project_start_date"
              type="date"
              className="aria-[invalid=true]:border-destructive"
              name="project_start_date"
              value={formData.project_start_date}
              onChange={handleChange}
              {...errorProps("project_start_date")}
            />
            <FieldError
              field="project_start_date"
              message={fieldErrors.project_start_date}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="project_end_date">Project end date</Label>
            <Input
              id="project_end_date"
              type="date"
              name="project_end_date"
              value={formData.project_end_date}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="relocating_from">
              Relocating from
              <RequiredMark />
            </Label>
            <Input
              id="relocating_from"
              type="text"
              className="aria-[invalid=true]:border-destructive"
              name="relocating_from"
              value={formData.relocating_from}
              onChange={handleChange}
              {...errorProps("relocating_from")}
            />
            <FieldError
              field="relocating_from"
              message={fieldErrors.relocating_from}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="relocating_to">Relocating to</Label>
            <Input
              id="relocating_to"
              type="text"
              name="relocating_to"
              value={formData.relocating_to}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="total_budget">Total budget</Label>
          <Input
            id="total_budget"
            type="number"
            name="total_budget"
            value={formData.total_budget}
            onChange={handleChange}
            min="0"
            placeholder="Enter total budget in AUD"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="project_notes">Internal notes · staff only</Label>
          <Textarea
            id="project_notes"
            name="project_notes"
            value={formData.project_notes}
            onChange={handleChange}
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleCloseForm}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Creating..." : "Create project"}
          </Button>
        </DialogFooter>
      </form>
    </div>
  );
};

export default ProjectForm;
